/**
 * BHL supplier code fallback (2026-10-15). Rendered by snippets/bhl-supplier-code.liquid; see its doc for where.
 *
 * Our part numbers are the supplier's code behind a prefix of ours (W-P91551, STX-435406), and the old site showed
 * them without the hyphen (WP91551, SX435406, the vsku: tags). Shopify's search does not find "P91551" inside
 * "W-P91551", but its field syntax does: "variants.sku:W-P91551 OR variants.sku:STX-P91551" returns exactly the
 * products whose SKU starts with one of those values (observed on b001gz-00, 2026-10-15; plain "A OR B" without the
 * field returns unrelated products, so the field form is required). One request to the predictive search JSON
 * (/search/suggest.json, at most 10 products) answers every candidate at once.
 *
 * A product is an EXACT match when its vsku: tag (or the end of its handle) equals the visible form of a candidate
 * (W + P91551 = WP91551, SX + 435406 = SX435406); the field search itself matches by prefix, so anything else it
 * returns is a PREFIX match (W-P9155 finds W-P91550 to W-P91557).
 *
 * The decisions are pure functions (lookupFor, pickMatches, decide) so they run under node in the tests; the element
 * only fetches and draws.
 */

/** [our SKU prefix, the old site's visible prefix]; product counts from data/derived/sku-prefixes.csv (2026-10-15). */
export const PREFIXES = [
  ['W', 'W'], // Wolseley 97,245
  ['STX', 'SX'], // Stax 45,852
  ['F', 'F'], // FPS 18,648
  ['DEC', 'DC'], // Decco 17,431
  ['PTS', 'PS'], // PTS 1,587
  ['TR', 'T'], // Towelrads 469
  ['STO', 'SO'], // Stove 159
  ['S', 'S'], // 86
];
export const MIN_LENGTH = 4;
export const MAX_LENGTH = 20;
export const PREDICTIVE_LIMIT = 5;

/** Identifier normal form (as snippets/bhl-normalise.liquid): casefold, no spaces, hyphens, dots or slashes. */
export function normalise(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-./]/g, '');
}

/**
 * The search to run for a query, or null when the query is not a bare supplier code: letters, digits and inner
 * hyphens, 4 to 20 characters, at least one digit, and not already one of our part numbers (a prefix of ours plus a
 * hyphen), which the normal search finds on its own.
 * @param {string} raw
 * @returns {{code: string, query: string, skus: string[], exact: Map<string, string>} | null}
 */
export function lookupFor(raw) {
  const code = String(raw ?? '').trim().toUpperCase();
  if (code.length < MIN_LENGTH || code.length > MAX_LENGTH) return null;
  if (!/^[A-Z0-9](?:[A-Z0-9-]*[A-Z0-9])?$/.test(code) || !/[0-9]/.test(code)) return null;
  if (PREFIXES.some(([ours]) => code.startsWith(ours + '-'))) return null;
  const skus = [];
  /** visible form (normalised) -> our part number */
  const exact = new Map();
  const add = (sku, visible) => {
    if (!skus.includes(sku)) skus.push(sku);
    const key = normalise(visible);
    if (!exact.has(key)) exact.set(key, sku);
  };
  // 1. the supplier's own code behind each prefix of ours: P91551 -> W-P91551 (visible WP91551), STX-P91551, ...
  for (const [ours, visible] of PREFIXES) add(`${ours}-${code}`, visible + code);
  // 2. the old site's visible part number typed as it was shown: WP91551 -> W-P91551, SX435406 -> STX-435406
  for (const [ours, visible] of PREFIXES) {
    if (code.startsWith(visible) && code.length - visible.length >= 3 && code[visible.length] !== '-') {
      add(`${ours}-${code.slice(visible.length)}`, code);
    }
  }
  return { code, query: skus.map((s) => `variants.sku:${s}`).join(' OR '), skus, exact };
}

/** Path of a product URL from the JSON, without the search tracking parameters. */
export function productPath(url, origin = 'https://x.invalid') {
  try {
    return new URL(url, origin).pathname;
  } catch (_) {
    return '';
  }
}

/**
 * Split the JSON products into exact matches (with our part number) and prefix matches, keeping the search order.
 * @param {Array<any>} products
 * @param {Map<string, string>} exact
 */
export function pickMatches(products, exact) {
  const out = { exact: [], prefix: [] };
  const seen = new Set();
  for (const product of products || []) {
    if (!product || !product.url || seen.has(product.url)) continue;
    seen.add(product.url);
    let sku = null;
    for (const tag of product.tags || []) {
      const t = String(tag);
      if (t.toLowerCase().startsWith('vsku:')) {
        const hit = exact.get(normalise(t.slice(5)));
        if (hit) {
          sku = hit;
          break;
        }
      }
    }
    if (!sku && product.handle) {
      const handle = String(product.handle).toLowerCase();
      for (const [key, value] of exact) {
        if (handle.endsWith('-' + key)) {
          sku = value;
          break;
        }
      }
    }
    if (sku) out.exact.push({ product, sku });
    else out.prefix.push({ product, sku: null });
  }
  return out;
}

/**
 * The matches without the products the page already shows (paths without query strings), so a product the normal
 * results list is not offered twice and an ambiguous code (F31903: F-31903 found normally, W-F31903 not) still shows
 * the other supplier's product.
 * @param {{exact: Array<any>, prefix: Array<any>}} picked
 * @param {Set<string>} shown
 */
export function withoutShown(picked, shown) {
  const keep = (m) => !shown.has(productPath(m.product.url));
  return { exact: picked.exact.filter(keep), prefix: picked.prefix.filter(keep) };
}

/**
 * What to do with the matches.
 *   page-zero:  one exact match from a complete answer -> open it; otherwise exact matches are listed (and the
 *               zero-results help hidden); no exact but prefix matches -> list those (the help stays); nothing -> nothing.
 *   page-few:   exact matches only, listed; never opens a product (the shopper already has results).
 *   predictive: exact matches, else up to PREDICTIVE_LIMIT prefix matches. One exact match is what Enter opens only
 *               when the answer is complete and the dropdown shows nothing else (`others`).
 * An answer is incomplete (`truncated`) when the JSON returned as many products as were asked for: another exact match
 * may be beyond the limit, so nothing is opened automatically.
 * @param {string} mode
 * @param {{exact: Array<any>, prefix: Array<any>}} picked
 * @param {{truncated?: boolean, others?: number}} [context]
 */
export function decide(mode, picked, { truncated = false, others = 0 } = {}) {
  const { exact, prefix } = picked;
  if (mode === 'page-zero') {
    if (exact.length === 1 && !truncated) return { action: 'open', kind: 'exact', items: exact };
    if (exact.length) return { action: 'show', kind: 'exact', items: exact, hideHelp: true };
    if (prefix.length) return { action: 'show', kind: 'prefix', items: prefix, hideHelp: false };
    return { action: 'none' };
  }
  if (mode === 'page-few') {
    return exact.length ? { action: 'show', kind: 'exact', items: exact, hideHelp: false } : { action: 'none' };
  }
  if (mode === 'predictive') {
    const single = exact.length === 1 && !truncated && others === 0;
    if (exact.length) return { action: 'show', kind: 'exact', items: exact, single, hideHelp: true };
    if (prefix.length) return { action: 'show', kind: 'prefix', items: prefix.slice(0, PREDICTIVE_LIMIT), single: false, hideHelp: true };
    return { action: 'none' };
  }
  return { action: 'none' };
}

/**
 * P210 (2026-10-26, Astra gate round 1 condition 1): order between this lookup and the manufacturer part number
 * lookup (assets/bhl-mpn-lookup.js) on a search page with no results. Both start at once; the supplier lookup may only
 * OPEN a product after the MPN lookup has answered:
 *   no MPN lookup on the page (null)                  -> open as before;
 *   MPN answered with no product, or only the product
 *   this lookup would open                            -> open;
 *   MPN answered with any other product               -> list the supplier matches instead (the MPN panel shows too);
 *   MPN not answered within MPN_WAIT_MS ('timeout')   -> list instead of opening (never navigate on a guess);
 *   MPN request failed ('failed': network, HTTP or a
 *   body that is not the expected JSON)               -> list instead of opening: unknown is not "no match"
 *                                                        (gate round 2, condition 1).
 * A product found by both lookups is listed once, in this panel (the MPN panel leaves it out; bhl:supplier-drawn).
 * Listing is the same drawing as several exact matches, so the outcome does not depend on which answer came first.
 */
export const MPN_WAIT_MS = 4000;

export function gateOpen(verdict, mpnPaths) {
  if (!verdict || verdict.action !== 'open') return verdict;
  if (mpnPaths === null) return verdict;
  const target = productPath(verdict.items[0].product.url);
  if (Array.isArray(mpnPaths) && mpnPaths.every((p) => p === target)) return verdict;
  return { action: 'show', kind: verdict.kind, items: verdict.items, hideHelp: true };
}

/** Resolve `ready` (a promise of product paths) or 'timeout' after `ms`, whichever comes first. */
export function waitFor(ready, ms = MPN_WAIT_MS) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve('timeout'), ms);
    Promise.resolve(ready).then(
      (paths) => {
        clearTimeout(timer);
        // gate round 2: 'failed' (the MPN request failed) stays distinct; anything malformed counts as a timeout
        resolve(Array.isArray(paths) || paths === 'failed' ? paths : 'timeout');
      },
      () => {
        clearTimeout(timer);
        resolve('timeout');
      }
    );
  });
}

/** The MPN lookup's answer on this page (its `ready` promise), null when the page has no MPN lookup. */
export function mpnChoice(root, ms = MPN_WAIT_MS) {
  const mpn = root?.querySelector?.('bhl-mpn-lookup[data-mode="page-zero"]');
  if (!mpn) return Promise.resolve(null);
  return waitFor(customElements.whenDefined('bhl-mpn-lookup').then(() => mpn.ready), ms);
}

export const TEXT = {
  checking: (code) => `Checking our part numbers for supplier code ${code}...`,
  opening: (title) => `Opening ${title}`,
  headingExact: (code, n) => (n === 1 ? `Product with supplier code ${code}` : `Products with supplier code ${code}`),
  headingPrefix: (code) => `Our part numbers that start with supplier code ${code}`,
  note: 'Our part numbers add a supplier prefix to the supplier’s own code, for example W-P91551 or STX-435406.',
  found: (code, n) => `${n} ${n === 1 ? 'product' : 'products'} found for supplier code ${code}`,
  part: (sku) => `Part no. ${sku}`,
};

/** cache: query -> {picked, truncated}, so a re-render of the same results draws again without a second request */
const cache = new Map();

class BhlSupplierCode extends HTMLElement {
  #abort = null;
  #observer = null;
  #listen = null;
  #input = null;
  #seq = 0;
  #gating = -1;

  connectedCallback() {
    // morph may replace our children (the server's markup for the same query): settle again from the cached answer
    this.#observer = new MutationObserver(() => this.#settleLater());
    this.#observer.observe(this, { childList: true });
    if (this.dataset.mode === 'predictive') {
      // a typed change makes our Enter target stale at once, before the new results arrive (debounce and fetch)
      this.#listen = new AbortController();
      this.#input = this.closest('predictive-search-component')?.querySelector('input[type="search"]') || null;
      this.#input?.addEventListener(
        'input',
        () => {
          if (!this.#inputMatches()) this.querySelectorAll('[data-single-result-url]').forEach((el) => el.remove());
          // typed back to this code: the server may send identical markup (no morph change), so settle again here
          else this.#settleLater();
        },
        { signal: this.#listen.signal }
      );
    }
    this.#run();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abort?.abort();
    this.#listen?.abort();
  }

  static get observedAttributes() {
    return ['data-code'];
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== null && oldValue !== newValue) this.#run();
  }

  get #status() {
    return this.querySelector('.bhl-sc__status');
  }

  /** predictive: the search input still holds this element's code (always true on the search page) */
  #inputMatches() {
    return !this.#input || normalise(this.#input.value) === normalise(this.dataset.code);
  }

  /** run `fn` with our own child changes hidden from the observer */
  #quietly(fn) {
    this.#observer?.disconnect();
    try {
      fn();
    } finally {
      if (this.isConnected) this.#observer?.observe(this, { childList: true });
    }
  }

  /** nothing to show (or the request failed): the element takes no space and the page stays as the server drew it */
  #clear() {
    this.#quietly(() => {
      this.removeAttribute('data-state');
      this.querySelector('.bhl-sc__panel')?.remove();
      if (this.#status) this.#status.textContent = '';
    });
    document.dispatchEvent(new CustomEvent('bhl:supplier-drawn'));
  }

  /** the results around this element: product paths already shown, and (predictive) how many other items there are */
  #onPage(mode) {
    const scope = mode === 'predictive' ? this.closest('[data-search-results]') : this.closest('results-list');
    const shown = new Set();
    let others = 0;
    if (!scope) return { shown, others };
    for (const a of scope.querySelectorAll('a[href*="/products/"]')) {
      // P210: the MPN lookup's panel is not "the results", so what this lookup lists never depends on which answered first
      if (!this.contains(a) && !a.closest('bhl-mpn-lookup')) shown.add(productPath(a.getAttribute('href') || '', location.origin));
    }
    if (mode === 'predictive') {
      for (const el of scope.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card')) {
        if (!this.contains(el)) others += 1;
      }
    }
    return { shown, others };
  }

  /** fetch the answer for the current code (once per query, cached), then settle */
  async #run() {
    this.#abort?.abort();
    const seq = ++this.#seq;
    const lookup = lookupFor(this.dataset.code);
    if (!lookup) {
      this.#clear();
      return;
    }
    if (!cache.has(lookup.query)) {
      if (this.dataset.mode === 'page-zero' && this.#status) {
        // the only mode that shows progress: the shopper is looking at "no results" while this runs
        this.#quietly(() => {
          this.setAttribute('data-state', 'checking');
          this.#status.textContent = TEXT.checking(lookup.code);
        });
      }
      const abort = new AbortController();
      this.#abort = abort;
      try {
        const limit = Number(this.dataset.limit) || 10;
        const url = new URL(this.dataset.endpoint || '/search/suggest.json', location.origin);
        url.searchParams.set('q', lookup.query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', String(limit));
        const response = await fetch(url, { signal: abort.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        const products = json?.resources?.results?.products || [];
        cache.set(lookup.query, { picked: pickMatches(products, lookup.exact), truncated: products.length >= limit });
      } catch (_) {
        if (!abort.signal.aborted && seq === this.#seq) this.#clear();
        return;
      }
    }
    if (seq === this.#seq) this.#settleLater();
  }

  /**
   * Decide from the cached answer and the DOM as it is now. Deferred to a new task, so a morph that is updating this
   * element (attributes first, then the results around it) has finished before the page is read.
   */
  #settleLater() {
    const seq = this.#seq;
    setTimeout(() => {
      if (seq === this.#seq && this.isConnected) this.#settle();
    }, 0);
  }

  #settle() {
    const lookup = lookupFor(this.dataset.code);
    const answer = lookup && cache.get(lookup.query);
    if (!answer) return; // still fetching (that run settles when it lands) or not a code
    if (!this.#inputMatches()) {
      // the shopper has typed on: this answer is stale; the next render brings the element for the new text
      this.querySelectorAll('[data-single-result-url]').forEach((el) => el.remove());
      return;
    }
    const mode = this.dataset.mode || '';
    const { shown, others } = this.#onPage(mode);
    const verdict = decide(mode, withoutShown(answer.picked, shown), { truncated: answer.truncated, others });
    if (verdict.action === 'none') {
      this.#clear();
      return;
    }
    if (verdict.action === 'open') {
      // P210 (gate round 1, condition 1): never open before the MPN lookup on the same page has answered, so a
      // product found by manufacturer part number is never discarded by the navigation (see gateOpen).
      const seq = this.#seq;
      if (this.#gating === seq) return; // already waiting for this answer (a re-render settles again)
      this.#gating = seq;
      mpnChoice(document).then((mpnPaths) => {
        if (seq !== this.#seq || !this.isConnected) return;
        const final = gateOpen(verdict, mpnPaths);
        if (final.action !== 'open') {
          this.#draw(lookup, final);
          return;
        }
        const { product } = final.items[0];
        this.#quietly(() => {
          if (this.#status) this.#status.textContent = TEXT.opening(product.title || final.items[0].sku);
          this.setAttribute('data-state', 'opening');
        });
        location.replace(productPath(product.url, location.origin));
      });
      return;
    }
    this.#draw(lookup, verdict);
  }

  #draw(lookup, verdict) {
    const mode = this.dataset.mode || '';
    const panel = document.createElement('section');
    panel.className = 'bhl-sc__panel';
    const heading = document.createElement(mode === 'predictive' ? 'h4' : 'h2');
    heading.className = mode === 'predictive' ? 'predictive-search-results__title' : 'bhl-sc__heading h5';
    heading.textContent =
      verdict.kind === 'exact' ? TEXT.headingExact(lookup.code, verdict.items.length) : TEXT.headingPrefix(lookup.code);
    panel.append(heading);
    if (mode !== 'predictive') {
      const note = document.createElement('p');
      note.className = 'bhl-sc__note';
      note.textContent = TEXT.note;
      panel.append(note);
    }
    const list = document.createElement('ul');
    list.className =
      mode === 'predictive' ? 'bhl-sc__list predictive-search-results__list list-unstyled' : 'bhl-sc__list';
    for (const { product, sku } of verdict.items) {
      const item = document.createElement('li');
      item.className = 'bhl-sc__item';
      if (product.id != null) item.dataset.productId = String(product.id);
      // predictive-search.js walks [ref="resultsItems[]"] inside .predictive-search-results__list with the arrow keys
      // (the card class is not used: it sizes items as carousel slides)
      if (mode === 'predictive') item.setAttribute('ref', 'resultsItems[]');
      const link = document.createElement('a');
      link.className = 'bhl-sc__link';
      link.href = productPath(product.url, location.origin);
      const src = product.featured_image?.url || product.image;
      const img = document.createElement('img');
      img.className = 'bhl-sc__img';
      img.alt = '';
      img.loading = 'lazy';
      img.width = 64;
      img.height = 64;
      if (src) {
        try {
          const u = new URL(src, location.origin);
          u.searchParams.set('width', '128');
          img.src = u.toString();
        } catch (_) {
          img.src = src;
        }
      }
      const text = document.createElement('span');
      text.className = 'bhl-sc__text';
      const title = document.createElement('span');
      title.className = 'bhl-sc__title';
      title.textContent = product.title || '';
      text.append(title);
      if (sku) {
        const part = document.createElement('span');
        part.className = 'bhl-sc__part';
        part.textContent = TEXT.part(sku);
        text.append(part);
      }
      link.append(img, text);
      item.append(link);
      list.append(item);
    }
    panel.append(list);
    if (verdict.single) {
      // predictive-search.js: Enter opens the first [data-single-result-url] in the results
      const single = document.createElement('div');
      single.dataset.singleResultUrl = productPath(verdict.items[0].product.url, location.origin);
      panel.append(single);
    }
    this.#quietly(() => {
      this.querySelector('.bhl-sc__panel')?.remove();
      // once found the status line is visually hidden (snippet CSS) but still announced
      if (this.#status) this.#status.textContent = TEXT.found(lookup.code, verdict.items.length);
      this.append(panel);
      this.setAttribute('data-state', 'found');
    });
    document.dispatchEvent(new CustomEvent('bhl:supplier-drawn'));
    if (verdict.hideHelp) this.#hideHelp(mode);
    if (mode === 'predictive') {
      // the dropdown now offers more than one product, so the theme's own single-result shortcut no longer holds:
      // Enter opens the search page (or our single exact match, set above only when nothing else is listed)
      this.closest('[data-search-results]')?.querySelectorAll('[data-single-result-url]').forEach((el) => {
        if (!this.contains(el)) el.removeAttribute('data-single-result-url');
      });
    }
  }

  #hideHelp(mode) {
    if (mode === 'page-zero') {
      const zero = this.parentElement?.querySelector(':scope > .bhl-zero');
      if (zero instanceof HTMLElement) BhlSupplierCode.#hide(zero);
    } else if (mode === 'predictive') {
      const results = this.closest('[data-search-results]');
      results?.querySelectorAll('.predictive-search-results__no-results, .bhl-predictive-help').forEach((el) => {
        if (el instanceof HTMLElement) BhlSupplierCode.#hide(el);
      });
    }
  }

  /** the theme CSS sets display on these blocks, which outranks the hidden attribute alone */
  static #hide(el) {
    el.hidden = true;
    el.style.display = 'none';
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('bhl-supplier-code')) {
  customElements.define('bhl-supplier-code', BhlSupplierCode);
}
